import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminPeoplePanel from "@/components/AdminPeoplePanel";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Users, BookOpen, ShoppingBag, MessageSquare, Activity,
  Trash2, Edit3, Check, X, RefreshCw, Settings, BarChart2,
  Mail, Shield, Zap,
} from "lucide-react";

const ADMIN_TABS = ["overview", "users", "people", "messages", "config", "errors"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [config, setConfig] = useState({});
  const [envStatus, setEnvStatus] = useState({});
  const [errors, setErrors] = useState([]);
  const [errorTotal, setErrorTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [role, setRole] = useState(null);
  const TABS = role === "viewer" ? ["people"] : ADMIN_TABS;

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    const { data } = await api.get("/admin/users?limit=50");
    setUsers(data.users || []);
  }, []);

  const loadMessages = useCallback(async () => {
    const { data } = await api.get("/admin/messages?limit=50");
    setMessages(data.messages || []);
  }, []);

  const loadConfig = useCallback(async () => {
    const { data } = await api.get("/admin/config");
    setConfig(data.config || {});
    setEnvStatus(data.env_status || {});
  }, []);

  const loadErrors = useCallback(async () => {
    const { data } = await api.get("/admin/errors?limit=100");
    setErrors(data.errors || []);
    setErrorTotal(data.total || 0);
  }, []);

  const clearErrors = async () => {
    await api.delete("/admin/errors");
    setErrors([]);
    setErrorTotal(0);
    toast.success("Error log cleared");
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/me");
        setRole(data.role);
        if (data.role === "viewer") setTab("people");
        else await loadStats();
      } catch (e) {
        setForbidden(true);
      }
      setLoading(false);
    })();
  }, [loadStats]);

  useEffect(() => {
    if (!stats) return;
    if (tab === "users") loadUsers();
    if (tab === "messages") loadMessages();
    if (tab === "config") loadConfig();
    if (tab === "errors") loadErrors();
  }, [tab, stats, loadUsers, loadMessages, loadConfig, loadErrors]);

  const updateUser = async (uid, changes) => {
    try {
      await api.patch(`/admin/users/${uid}`, changes);
      toast.success("User updated");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteUser = async (uid, name) => {
    if (!window.confirm(`Delete ${name}? This is irreversible.`)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("User deleted");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const markMessage = async (id, status) => {
    await api.patch(`/admin/messages/${id}`, { status });
    loadMessages();
  };

  const runCleanup = async () => {
    try {
      const { data } = await api.post("/admin/cleanup-r2");
      toast.success(`R2 cleanup done: ${JSON.stringify(data)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    }
  };

  const saveConfig = async (key, value) => {
    try {
      await api.post("/admin/config", { key, value });
      toast.success("Config saved");
      loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  if (loading) return (
      <div className="flex items-center justify-center h-64 text-[#B0AEBC]">Loading…</div>
  );

  if (forbidden) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Shield size={40} className="text-red-400" />
        <p className="text-[#B0AEBC]">Admin access required.</p>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#1a1a2e] border border-[#B8A4FF]/20 flex items-center justify-center">
            <Shield size={18} className="text-[#B8A4FF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-[13px] text-[#B0AEBC]">Platform management for Studivault</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#131316] border border-[#2a2a3e] rounded-xl p-1 mb-8 w-fit">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-[13px] font-medium capitalize transition-all ${
                tab === t ? "bg-[#1a1a2e] text-white border border-[#2a2a3e]" : "text-[#B0AEBC] hover:text-white"
              }`}
              data-testid={`admin-tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {tab === "people" && <AdminPeoplePanel />}

        {tab === "overview" && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Users", value: stats.users.total, sub: `+${stats.users.new_this_month} this month`, icon: <Users size={18} />, color: "text-blue-400" },
                { label: "Paid Plans", value: stats.users.paid_plans, sub: "Active subscriptions", icon: <Zap size={18} />, color: "text-yellow-400" },
                { label: "Studies", value: stats.studies.total, sub: `+${stats.studies.new_this_month} this month`, icon: <BookOpen size={18} />, color: "text-green-400" },
                { label: "Packs", value: stats.packs.total, sub: `+${stats.packs.new_this_month} published`, icon: <ShoppingBag size={18} />, color: "text-purple-400" },
                { label: "Exams", value: stats.exams.total, sub: `+${stats.exams.new_this_month} this month`, icon: <Activity size={18} />, color: "text-red-400" },
                { label: "Messages", value: stats.messages.total, sub: `${stats.messages.new_unread} unread`, icon: <Mail size={18} />, color: "text-orange-400" },
                { label: "LLM Calls", value: stats.llm.calls_this_month, sub: `$${stats.llm.est_cost_usd} est. cost`, icon: <BarChart2 size={18} />, color: "text-cyan-400" },
              ].map(s => (
                <div key={s.label} className="card p-4">
                  <div className={`${s.color} mb-2`}>{s.icon}</div>
                  <p className="text-2xl font-bold text-white">{s.value}</p>
                  <p className="text-[11px] text-[#B0AEBC] mt-0.5">{s.label}</p>
                  <p className="text-[11px] text-[#6B6B7B] mt-1">{s.sub}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button onClick={runCleanup} className="btn btn-outline px-4 py-2 text-[13px] flex items-center gap-2">
                <RefreshCw size={14} /> R2 Cleanup
              </button>
              <button onClick={() => { loadStats(); toast.success("Stats refreshed"); }}
                className="btn btn-outline px-4 py-2 text-[13px] flex items-center gap-2">
                <RefreshCw size={14} /> Refresh Stats
              </button>
            </div>
          </motion.div>
        )}

        {/* Users tab */}
        {tab === "users" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="overflow-x-auto rounded-xl border border-[#2a2a3e]">
              <table className="w-full text-[13px]">
                <thead className="bg-[#131316]">
                  <tr className="text-[#B0AEBC] text-left">
                    <th className="px-4 py-3 font-medium">User</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium">Level</th>
                    <th className="px-4 py-3 font-medium">Gems</th>
                    <th className="px-4 py-3 font-medium">Streak</th>
                    <th className="px-4 py-3 font-medium">2FA</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2a2a3e]">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-[#131316]/60 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-white">{u.full_name}</p>
                        <p className="text-[11px] text-[#6B6B7B]">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                          u.plan === "pro" ? "bg-[#B8A4FF]/10 text-[#B8A4FF]" :
                          u.plan === "plus" ? "bg-blue-400/10 text-blue-400" :
                          "bg-[#2a2a3e] text-[#B0AEBC]"
                        }`}>{u.plan}</span>
                      </td>
                      <td className="px-4 py-3 text-white">{u.level}</td>
                      <td className="px-4 py-3 text-yellow-400">{u.gems}</td>
                      <td className="px-4 py-3 text-orange-400">{u.streak_count}d</td>
                      <td className="px-4 py-3">
                        {u.totp_enabled ? <Check size={14} className="text-green-400" /> : <X size={14} className="text-[#4a4a5a]" />}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <select
                            className="bg-[#131316] border border-[#2a2a3e] text-[12px] rounded px-1 py-0.5 text-[#B0AEBC]"
                            defaultValue={u.plan}
                            onChange={e => updateUser(u.id, { plan: e.target.value })}
                          >
                            <option value="free">free</option>
                            <option value="plus">plus</option>
                            <option value="pro">pro</option>
                          </select>
                          <button
                            onClick={() => deleteUser(u.id, u.full_name)}
                            className="p-1 rounded text-red-400 hover:bg-red-400/10 transition-colors"
                            title="Delete user"
                            data-testid={`admin-delete-user-${u.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Messages tab */}
        {tab === "messages" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {messages.length === 0 && (
              <p className="text-[#B0AEBC] text-center py-12">No messages yet.</p>
            )}
            {messages.map(m => (
              <div key={m.id} className={`card p-4 ${m.status === "new" ? "border-[#B8A4FF]/30" : ""}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                        m.status === "new" ? "bg-[#B8A4FF]/10 text-[#B8A4FF]" :
                        m.status === "resolved" ? "bg-green-400/10 text-green-400" :
                        "bg-[#2a2a3e] text-[#B0AEBC]"
                      }`}>{m.status}</span>
                      <span className="text-[11px] text-[#6B6B7B] capitalize">{m.topic}</span>
                      <span className="text-[11px] text-[#6B6B7B]">·</span>
                      <span className="text-[11px] text-[#6B6B7B]">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-[13px] text-white font-medium">{m.name || m.email}</p>
                    <p className="text-[12px] text-[#B0AEBC]">{m.email}</p>
                    <p className="text-[13px] text-[#D4D4D8] mt-2 whitespace-pre-line">{m.message}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {m.status === "new" && (
                      <button onClick={() => markMessage(m.id, "read")}
                        className="p-1.5 rounded text-blue-400 hover:bg-blue-400/10 transition-colors" title="Mark read">
                        <Check size={13} />
                      </button>
                    )}
                    {m.status !== "resolved" && (
                      <button onClick={() => markMessage(m.id, "resolved")}
                        className="p-1.5 rounded text-green-400 hover:bg-green-400/10 transition-colors" title="Resolve">
                        <Check size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* Config tab */}
        {tab === "config" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Env status */}
            <div className="card p-4">
              <h3 className="text-[14px] font-semibold text-white mb-3">Environment Variables</h3>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(envStatus).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-[12px] text-[#B0AEBC] font-mono">{k}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded ${
                      v === "configured" ? "bg-green-400/10 text-green-400" : "bg-red-400/10 text-red-400"
                    }`}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Global Free Mode */}
            <div className="card p-4" data-testid="admin-free-mode-card">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-[14px] font-semibold text-white">🎉 Free Mode (unlock everything)</h3>
                  <p className="text-[12.5px] text-[#B0AEBC] mt-1 max-w-md">
                    When ON, every user gets all Plus &amp; Pro features for free and a banner appears on pricing.
                    Turn OFF to return to normal paid plans.
                  </p>
                </div>
                {(() => {
                  const on = String(config.free_mode || "off").toLowerCase() === "on";
                  return (
                    <button
                      onClick={() => saveConfig("free_mode", on ? "off" : "on")}
                      data-testid="admin-free-mode-toggle"
                      aria-label="Toggle free mode"
                      className="w-14 h-8 rounded-full flex-shrink-0 transition-colors relative"
                      style={{ background: on ? "#7C5CFF" : "rgba(128,128,150,0.35)" }}
                    >
                      <span className="absolute top-1 w-6 h-6 rounded-full bg-white transition-all"
                        style={{ left: on ? "26px" : "4px" }} />
                    </button>
                  );
                })()}
              </div>
              <div className="mt-3 text-[12px]">
                Status: <span className={String(config.free_mode || "off").toLowerCase() === "on" ? "text-green-400 font-semibold" : "text-[#B0AEBC]"}>
                  {String(config.free_mode || "off").toLowerCase() === "on" ? "FREE MODE ACTIVE" : "Normal (paid plans)"}
                </span>
              </div>
            </div>

            {/* DB config */}
            <div className="card p-4">
              <h3 className="text-[14px] font-semibold text-white mb-3">Database Config</h3>
              <div className="space-y-3">
                {[
                  { key: "adsense_client", label: "Google AdSense Client ID", placeholder: "ca-pub-xxxxxxxxxxxxxxxx" },
                  { key: "adsense_slot", label: "Google AdSense Slot ID", placeholder: "1234567890" },
                  { key: "site_announcement", label: "Site Announcement Banner", placeholder: "Leave empty to hide" },
                  { key: "deepseek_model_flash", label: "AI Flash Model", placeholder: "deepseek-chat" },
                ].map(({ key, label, placeholder }) => (
                  <ConfigField key={key} fieldKey={key} label={label} placeholder={placeholder}
                    defaultValue={config[key] || ""} onSave={saveConfig} />
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Errors tab */}
        {tab === "errors" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="admin-errors-panel">
            <div className="flex items-center justify-between">
              <h3 className="text-[14px] font-semibold text-white">
                Server errors <span className="text-[#8E8B99] font-normal">({errorTotal} total)</span>
              </h3>
              <div className="flex gap-2">
                <button onClick={loadErrors} data-testid="admin-errors-refresh"
                  className="text-[12px] px-3 py-1.5 rounded-lg border border-[#2a2a3e] text-[#B0AEBC] hover:text-white">
                  Refresh
                </button>
                {errors.length > 0 && (
                  <button onClick={clearErrors} data-testid="admin-errors-clear"
                    className="text-[12px] px-3 py-1.5 rounded-lg border border-red-400/30 text-red-400 hover:bg-red-400/10">
                    Clear log
                  </button>
                )}
              </div>
            </div>
            {errors.length === 0 ? (
              <div className="card p-8 text-center text-[13px] text-[#8E8B99]" data-testid="admin-errors-empty">
                No errors logged. 🎉 The app is healthy.
              </div>
            ) : (
              <div className="space-y-2">
                {errors.map((e, i) => (
                  <details key={e.id || i} className="card p-3 text-[12.5px]" data-testid={`admin-error-${i}`}>
                    <summary className="cursor-pointer flex items-center gap-3 list-none">
                      <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-red-400/10 text-red-400">{e.method}</span>
                      <span className="font-mono text-[#B0AEBC] truncate flex-1">{e.path}</span>
                      <span className="text-[#8E8B99] text-[11px] shrink-0">{(e.created_at || "").replace("T", " ").slice(0, 19)}</span>
                    </summary>
                    <p className="text-red-300 mt-2 font-medium">{e.error}</p>
                    <pre className="mt-2 p-2 bg-black/40 rounded text-[10.5px] text-[#8E8B99] overflow-x-auto whitespace-pre-wrap">{e.traceback}</pre>
                  </details>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
  );
}

function ConfigField({ fieldKey, label, placeholder, defaultValue, onSave }) {
  const [val, setVal] = useState(defaultValue);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(fieldKey, val);
    setSaving(false);
  };

  return (
    <div>
      <label className="text-[12px] font-medium text-[#B0AEBC] mb-1 block">{label}</label>
      <div className="flex gap-2">
        <input
          className="input flex-1 text-[13px]"
          value={val}
          onChange={e => setVal(e.target.value)}
          placeholder={placeholder}
        />
        <button onClick={save} disabled={saving}
          className="btn btn-outline px-3 py-1.5 text-[12px] flex items-center gap-1">
          <Check size={12} /> {saving ? "…" : "Save"}
        </button>
      </div>
    </div>
  );
}
